import { Box, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  getEligibleFeatures,
  sumAllocatedSeats,
} from '@components/PatientPlans';
import PatientPlanTable from '@components/Tables/PatientPlanTable';
import type { PlanRowData } from '@components/Tables/PatientPlanTable/PatientPlanTable';

import type { ListPurchasedPlansQuery } from 'generated/plan.generated';
import type { OrgFeatureModel } from 'generated/types';

import type { LicensePlanValues } from '../types';
import PlanEditDialog from './PlanEditDialog';

const messages = defineMessages({
  seatPoolExceeded: {
    id: 'orgSettings.licensePlan.seatPoolExceeded',
    defaultMessage:
      'Plans allocate {allocated} seats, more than the {total} in the organization’s pool.',
  },
});

interface PatientPlansTabProps {
  /** The full feature catalog for this org, with its saved enabled state */
  features: OrgFeatureModel[];
  /** Patient status counts per plan, joined on plan_id */
  purchasedPlans: ListPurchasedPlansQuery['listPurchasedPlans'];
  /** The seat pool as currently entered; null until one is configured */
  totalSeats?: number | null;
}

/**
 * The org's patient plans as a counts table over the page's form; each row's
 * pencil opens one `PatientPlanCard` in a dialog, which owns the table so it
 * can drive its own open state. Eligibility comes from the form's
 * `org_feature_ids` rather than saved state, so a feature enabled on the other
 * tab is immediately selectable.
 */
export default function PatientPlansTab({
  features,
  purchasedPlans,
  totalSeats,
}: PatientPlansTabProps) {
  const { formatMessage } = useIntl();
  const { isSubmitting, values } = useFormikContext<LicensePlanValues>();

  const eligibleFeatures = useMemo(
    () => getEligibleFeatures(features, values.org_feature_ids),
    [features, values.org_feature_ids]
  );

  /** Status counts live on the purchased-plan rows the plans also write. */
  const countsByPlanId = useMemo(
    () =>
      new Map(
        purchasedPlans.map(({ plan_id, user_plans }) => [plan_id, user_plans])
      ),
    [purchasedPlans]
  );

  const allocated = sumAllocatedSeats(values.plans);

  // Read from values rather than `errors.plans`: Formik writes per-plan errors
  // over the array-level message with `setIn`, so the seat-pool string
  // disappears when a plan is also invalid.
  const seatPoolError =
    typeof totalSeats === 'number' && allocated > totalSeats
      ? formatMessage(messages.seatPoolExceeded, {
          allocated,
          total: totalSeats,
        })
      : '';

  const rows: PlanRowData[] = values.plans.map((plan) => {
    const counts = plan.plan_id ? countsByPlanId.get(plan.plan_id) : undefined;
    const seats = Number(plan.seats) || 0;

    return {
      local_id: plan.local_id,
      name: plan.name,
      seats,
      features_selected: plan.feature_ids.length,
      features_total: eligibleFeatures.length,
      seats_percent:
        totalSeats && totalSeats > 0
          ? Math.round((seats / totalSeats) * 100)
          : null,
      new_users: counts?.new ?? 0,
      invited_users: counts?.invited ?? 0,
      active_users: counts?.active ?? 0,
      total_assigned: counts?.total ?? plan.assigned_seats ?? 0,
    };
  });

  const patientCount = rows.reduce((sum, row) => sum + row.total_assigned, 0);

  return (
    <Stack gap={2}>
      <PlanEditDialog
        disabled={isSubmitting}
        eligibleFeatures={eligibleFeatures}
      >
        {({ addPlan, editPlan }) => (
          <PatientPlanTable
            allocated={allocated}
            data={rows}
            disabled={isSubmitting}
            onAdd={addPlan}
            onEdit={editPlan}
            patientCount={patientCount}
            totalSeats={totalSeats}
          />
        )}
      </PlanEditDialog>

      {seatPoolError && (
        <Box>
          <Typography
            color="error"
            data-testid="seat-pool-error"
            variant="body2"
          >
            {seatPoolError}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
